import React from 'react'
import { Invoice, Paginator, Role } from '@/types'
import { useTranslation } from 'react-i18next'
import { InvoiceService } from '@/lib/services/invoice'
import { useToast } from '@/hooks/use-toast'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'
import { format } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Button } from '../ui/button'
import { EllipsisVertical, Euro, FileDown } from 'lucide-react'
import Pagination from '../pagination'
import useAuth from '@/context/useAuth'

interface InvoiceListProps {
  invoices: Paginator<Invoice>
  onDownload: (invoice: Invoice) => void
  setInvoices: (invoices: Paginator<Invoice>) => void
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  onDownload,
  setInvoices,
}) => {
  const { t } = useTranslation('invoices')
  const { toast } = useToast()
  const { user } = useAuth()

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800 hover:bg-green-200'
      case 'pending':
        return 'bg-red-100 text-red-800 hover:bg-red-200'
      case 'failed':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
    }
  }

  const onMarkAsUnpaid = async (invoice: Invoice) => {
    try {
      await InvoiceService.markAsUnpaid(invoice.id)
      toast({
        title: t('invoice_marked_as_unpaid'),
        variant: 'success',
      })
      setInvoices({
        ...invoices,
        data: invoices.data.map((i) => {
          if (i.id === invoice.id) {
            return { ...i, status: 'pending' }
          }
          return i
        }),
      })
    } catch (error) {
      toast({
        title: t('invoice_mark_as_unpaid_failed'),
        variant: 'destructive',
      })
    }
  }

  const onMarkAsPaid = async (invoice: Invoice) => {
    try {
      await InvoiceService.markAsPaid(invoice.id)
      toast({
        title: t('invoice_marked_as_paid'),
        variant: 'success',
      })
      setInvoices({
        ...invoices,
        data: invoices.data.map((i) => {
          if (i.id === invoice.id) {
            return { ...i, status: 'paid' }
          }
          return i
        }),
      })
    } catch (error) {
      toast({
        title: t('invoice_mark_as_paid_failed'),
        variant: 'destructive',
      })
    }
  }

  const formatAmount = (invoice: Invoice) => {
    // 检查哪个属性存在
    const amount =
      invoice.totalAmount !== undefined
        ? invoice.totalAmount
        : invoice.tvaAmount

    if (amount === undefined) return '€0.00'
    return `€${amount.toFixed(2)}`
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('user')}</TableHead>
              <TableHead>{t('invoice_number')}</TableHead>
              <TableHead>{t('period')}</TableHead>
              <TableHead>{t('status')}</TableHead>
              <TableHead>{t('amount')}</TableHead>
              <TableHead>{t('created_at')}</TableHead>
              <TableHead>{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.data?.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="font-medium">
                  [{invoice.user?.company_name}] <br />
                  {invoice.user?.last_name} {invoice.user?.first_name}
                </TableCell>
                <TableCell className="font-medium">
                  {invoice.invoiceNumber}
                </TableCell>
                <TableCell>
                  {format(new Date(invoice.startDate), 'dd/MM/yyyy')} -{' '}
                  {format(new Date(invoice.endDate), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(invoice.status)}>
                    {t(`status.${invoice.status}`)}
                  </Badge>
                </TableCell>
                <TableCell>{formatAmount(invoice)}</TableCell>
                <TableCell>
                  {format(new Date(invoice.createdAt), 'dd/MM/yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="rounded-full"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {user?.role === Role.Admin && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            invoice.status === 'paid'
                              ? onMarkAsUnpaid(invoice)
                              : onMarkAsPaid(invoice)
                          }}
                        >
                          <Euro className="h-4 w-4" />
                          {invoice.status === 'paid'
                            ? t('mark_as_unpaid')
                            : t('mark_as_paid')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onDownload(invoice)
                        }}
                      >
                        <FileDown className="h-4 w-4" />
                        {t('download')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}

            {invoices.data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8 text-gray-500"
                >
                  {t('no_invoices_found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination lastPage={invoices.meta.last_page} />
    </>
  )
}

export default InvoiceList
